import React, { useState } from "react";
import { Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { EVENT_CHANGES, getEventLabelsFromEvent } from "@/utils/eventLabels";
// ProjectToggleButton removed
import ReportErrorDialog from "./ReportErrorDialog";
import EventImageViewer from "./EventImageViewer";
import { PricingEvent } from "@/store/eventsSlice";
import { useAppSelector } from "@/hooks/useAppSelector";
import { versionToQuarter } from "@/utils/versionUtils";

interface EventViewCardProps {
  company: PricingEvent;
  isInSwipefile?: boolean;
  onToggle?: (e: React.MouseEvent) => void;
}

const EventViewCard = ({ company, isInSwipefile, onToggle }: EventViewCardProps) => {
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  // Get the latest event data from Redux store
  const latestEvent = useAppSelector(state =>
    state.events.events.find(e =>
      e.slug === company.slug &&
      e.v1 === company.v1 &&
      e.v2 === company.v2
    )
  );

  const getQuarter = (version: string) => {
    if (!version) return '';
    const quarterStr = versionToQuarter(version);
    if (!quarterStr) return '';
    const year = quarterStr.slice(0, 4);
    const quarter = quarterStr.slice(5); // Get the Q1, Q2, etc. part
    return `${quarter} ${year}`;
  };

  const eventLabels = getEventLabelsFromEvent(company);

  return (
    <div className="flex flex-col w-full">
      <div className="sticky top-0 z-10 bg-white p-4">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-4">
            <div className="relative w-12 h-12 flex-shrink-0">
              <img
                src={company.logoUrl}
                alt={`${company.slug} logo`}
                className="w-full h-full object-contain"
              />
              <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm border-2 border-white">
                <Zap className="h-4 w-4 text-yellow-500" />
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold capitalize">
                {company.slug.replace(/-/g, ' ')}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {company.description}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Changed in {getQuarter(company.v1)}
              </p>
              <div className="flex flex-wrap gap-2 mt-2 mb-5">
                {eventLabels.map((label, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className={`flex items-center gap-1 ${label.className}`}
                  >
                    {label.icon && <label.icon className="h-3 w-3" />}
                    {label.label}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          {/* Project toggle removed */}
        </div>

        <EventImageViewer company={company} />
      </div>

      <ReportErrorDialog
        isOpen={isReportModalOpen}
        onOpenChange={setIsReportModalOpen}
        company={company}
        existingError={latestEvent?.error}
      />
    </div>
  );
};

export default EventViewCard;